import React, { useState } from 'react';
import Button from 'components/atoms/Button';
import TextArea from 'components/atoms/TextArea';
import Icon from 'components/atoms/Icon';

interface VerificationInputProps {
  onSubmit: (additionalInfo?: string) => void;
  isSubmitting?: boolean;
  placeholder?: string;
  helpText?: string;
}

const VerificationInput: React.FC<VerificationInputProps> = ({
  onSubmit,
  isSubmitting = false,
  placeholder = 'Provide additional information or corrections (optional)...',
  helpText
}) => {
  const [response, setResponse] = useState('');

  const handleProvideInfo = (e: React.FormEvent) => {
    e.preventDefault();

    if (response.trim() && !isSubmitting) {
      onSubmit(response.trim());
      setResponse('');
    }
  };

  const handleContinue = () => {
    if (!isSubmitting) {
      onSubmit(); // Call with no parameters to continue with available data
    }
  };

  return (
    <div className="w-full max-w-3xl">
      <form onSubmit={handleProvideInfo}>
        <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
          {helpText && (
            <div className="bg-gray-50 rounded-lg p-3 mb-3 flex gap-2">
              <Icon name="info" className="w-4 h-4 text-gray-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-gray-700">{helpText}</p>
            </div>
          )}

          <TextArea
            placeholder={placeholder}
            value={response}
            onChange={(e) => setResponse(e.target.value)}
            rows={6}
            className="w-full border-none outline-none resize-none text-sm leading-6 text-gray-800 bg-transparent font-inherit p-0 mb-3"
            disabled={isSubmitting}
          />

          <div className="flex items-center justify-between">
            <div className="text-xs text-gray-500">
              <Icon name="information-circle" className="w-3.5 h-3.5 inline mr-1" />
              Your information is encrypted and secure
            </div>

            <div className="flex gap-2">
              <Button
                type="button"
                variant="teal"
                className="px-5 py-2.5 font-satoshi font-medium text-sm leading-[130%] tracking-normal align-middle rounded-full"
                onClick={handleContinue}
                disabled={isSubmitting}
              >
                Run Classification
              </Button>

              <Button
                type="submit"
                variant="gray-outline"
                className="px-5 py-2.5 font-satoshi font-medium text-sm leading-[130%] tracking-normal align-middle border-[#CFCFCF] rounded-full"
                disabled={!response.trim() || isSubmitting}
                loading={isSubmitting}
              >
                Edit Product Info
              </Button>
            </div>
          </div>
        </div>
      </form>

      <div className="text-center mt-3">
        <p className="text-xs text-gray-400">
          Review the extracted data above and choose whether to continue or provide corrections
        </p>
      </div>
    </div>
  );
};

export default VerificationInput;
